import { useEffect, useState } from "react";
import { Banner, getActivePopup } from "../services/banners";

interface UseMarketingPopupReturn {
  popup: Banner | null;
  isOpen: boolean;
  closePopup: () => void;
}

/**
 * Hook para gerenciar a exibição do popup de marketing
 * 
 * Funcionalidades:
 * - Busca o popup ativo do backend
 * - Aplica delay antes de exibir
 * - Controla frequência de exibição (once/daily/always)
 * - Armazena informações no localStorage
 */
export const useMarketingPopup = (): UseMarketingPopupReturn => {
  const [popup, setPopup] = useState<Banner | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  useEffect(() => {
    loadPopup();
  }, []);

  const loadPopup = async () => {
    try {
      const activePopup = await getActivePopup();
      
      if (!activePopup) {
        return;
      }

      // Verificar se deve exibir baseado na frequência
      if (!shouldShowPopup(activePopup)) {
        return;
      }

      setPopup(activePopup);

      // Aplicar delay antes de exibir
      const delay = activePopup.displayDelay * 1000; // converter para ms
      setTimeout(() => {
        setIsOpen(true);
      }, delay);
    } catch (error) {
      console.error("Erro ao carregar popup:", error);
    }
  };

  const shouldShowPopup = (popup: Banner): boolean => {
    const storageKey = `popup_${popup.id}`;
    const now = new Date().getTime();

    const storedData = localStorage.getItem(storageKey);

    // Se não há dados armazenados, pode exibir
    if (!storedData) {
      return true;
    }

    try {
      const { lastShown, frequency } = JSON.parse(storedData);

      switch (popup.displayFrequency) {
        case "once":
          // Já foi exibido uma vez nesta sessão/dispositivo
          return false;

        case "daily":
          // Verificar se passou 24 horas
          const oneDayInMs = 24 * 60 * 60 * 1000;
          return now - lastShown > oneDayInMs;

        case "always":
          // Sempre exibir (mas apenas uma vez por sessão)
          const sessionKey = `popup_session_${popup.id}`;
          if (sessionStorage.getItem(sessionKey)) {
            return false;
          }
          sessionStorage.setItem(sessionKey, "true");
          return true;

        default:
          return true;
      }
    } catch (error) {
      // Se houver erro ao parsear, permitir exibição
      return true;
    }
  };

  const closePopup = () => {
    if (!popup) return;

    setIsOpen(false);

    // Registrar que o popup foi exibido
    const storageKey = `popup_${popup.id}`;
    const now = new Date().getTime();

    localStorage.setItem(
      storageKey,
      JSON.stringify({
        lastShown: now,
        frequency: popup.displayFrequency,
      })
    );
  };

  return {
    popup,
    isOpen,
    closePopup,
  };
};

